import _ from 'lodash';
import { FormattedDate, FormattedMessage } from 'react-intl';

import messages from './RepaymentScheduleTable.messages';
import styles from './RepaymentScheduleTable.module.css';
import { FormattedCurrency } from '../../../../../iwIntlProvider/FormattedCurrency';
import IwMedia from '../../../../../iwMedia/IwMedia';
import { NormalisedRepaymentSchedule } from '../../../MyOffers.types';

const RepaymentScheduleTable = ({
    repaymentSchedule
}: {
    repaymentSchedule: NormalisedRepaymentSchedule;
}) => {
    const renderTableHeader = () => {
        return (
            <tr>
                <IwMedia to="small">
                    <th>
                        <FormattedMessage {...messages.date} />
                    </th>
                    <th colSpan={2}>
                        <FormattedMessage {...messages.split} />
                    </th>
                    <th>
                        <FormattedMessage {...messages.total} />
                    </th>
                </IwMedia>
                <IwMedia from="small">
                    <th>
                        <FormattedMessage {...messages.date} />
                    </th>
                    <th>
                        <FormattedMessage {...messages.principal} />
                    </th>
                    <th>
                        <FormattedMessage {...messages.interest} />
                    </th>
                    {repaymentSchedule.hasFees && (
                        <th>
                            <FormattedMessage {...messages.fees} />
                        </th>
                    )}
                    <th>
                        <FormattedMessage {...messages.totalRepayment} />
                    </th>
                </IwMedia>
            </tr>
        );
    };
    const renderTableBody = () => {
        const rows = _.map(repaymentSchedule.repayments, (repayment) => {
            return (
                <tr key={repayment.date} data-testid="repayment-row">
                    <IwMedia to="small">
                        <td className={styles.date}>
                            <FormattedDate
                                value={new Date(repayment.date)}
                                day="numeric"
                                month="numeric"
                                year="2-digit"
                            />
                        </td>
                        <td colSpan={2}>
                            <span className={styles.principalLabel}>
                                <FormattedMessage {...messages.principal} />:
                            </span>
                            <FormattedCurrency value={repayment.principal} />
                            <br />
                            <span className={styles.interestLabel}>
                                <FormattedMessage {...messages.interest} />:
                            </span>
                            <FormattedCurrency value={repayment.interest} />
                            {repaymentSchedule.hasFees && (
                                <>
                                    <br />
                                    <span className={styles.feesLabel}>
                                        <FormattedMessage {...messages.fees} />:
                                    </span>
                                    <FormattedCurrency
                                        value={repayment.total_fees}
                                    />
                                </>
                            )}
                        </td>
                        <td className={styles.total}>
                            <FormattedCurrency value={repayment.total} />
                        </td>
                    </IwMedia>
                    <IwMedia from="small">
                        <td>
                            <FormattedDate value={new Date(repayment.date)} />
                        </td>
                        <td>
                            <FormattedCurrency value={repayment.principal} />
                        </td>
                        <td>
                            <FormattedCurrency value={repayment.interest} />
                        </td>
                        {repaymentSchedule.hasFees && (
                            <td>
                                <FormattedCurrency
                                    value={repayment.total_fees}
                                />
                            </td>
                        )}
                        <td>
                            <FormattedCurrency value={repayment.total} />
                        </td>
                    </IwMedia>
                </tr>
            );
        });

        if (repaymentSchedule.repayments.length < 12) {
            return [
                ...rows,
                // eslint-disable-next-line react/jsx-key
                <tr>
                    <td colSpan={4}>
                        <div className={styles.earlyRepayment}>
                            <p>
                                <FormattedMessage {...messages.payOff} />
                            </p>
                            <p>
                                <FormattedMessage {...messages.extraPayments} />
                            </p>
                        </div>
                    </td>
                </tr>
            ];
        }

        return rows;
    };

    const renderTableFooter = () => {
        return (
            <tr>
                <IwMedia to="small">
                    <td colSpan={3}>
                        <FormattedMessage {...messages.total} />:
                    </td>
                </IwMedia>
                <IwMedia from="small">
                    <td>
                        <FormattedMessage {...messages.total} />:
                    </td>
                    <td data-testid="total-principal">
                        <FormattedCurrency
                            // @ts-expect-error
                            value={repaymentSchedule.totalPrincipal}
                        />
                    </td>
                    <td data-testid="total-interest">
                        <FormattedCurrency
                            // @ts-expect-error
                            value={repaymentSchedule.totalInterest}
                        />
                    </td>
                    {repaymentSchedule.hasFees && (
                        <td>
                            <FormattedCurrency
                                // @ts-expect-error
                                value={repaymentSchedule.totalFees}
                            />
                        </td>
                    )}
                </IwMedia>
                <td data-testid="total-balance">
                    <FormattedCurrency
                        // @ts-expect-error
                        value={repaymentSchedule.totalRepayment}
                    />
                </td>
            </tr>
        );
    };

    return (
        <div className={styles.RepaymentScheduleTable}>
            <table>
                <thead>{renderTableHeader()}</thead>
                <tbody>{renderTableBody()}</tbody>
                <tfoot>{renderTableFooter()}</tfoot>
            </table>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default RepaymentScheduleTable;
